import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapPin, Mail, FileText, ArrowLeft } from 'lucide-react';
import useSEO from '@/hooks/useSEO';
import { slugify } from '@/lib/utils';
import { AVAILABILITY_MESSAGE } from '@/components/BeerCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function useProductJsonLd(beer) {
  useEffect(() => {
    if (!beer) return;
    const id = 'beer-jsonld';
    const data = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `${beer.name} - ${beer.style}`,
      description: beer.long_description,
      image: beer.image_url,
      category: 'Bière artisanale',
      brand: { '@type': 'Brand', name: 'Soif de Provence' },
      manufacturer: {
        '@type': 'Organization',
        name: 'Soif de Provence',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Saint-Rémy-de-Provence',
          postalCode: '13210',
          addressCountry: 'FR',
        },
      },
    };
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [beer]);
}

export default function BeerDetail() {
  const { slug } = useParams();
  const [beer, setBeer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBeer = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        const match = response.data.find((p) => !p.is_bundle && slugify(p.name) === slug);
        setBeer(match || null);
      } catch (error) {
        // erreur de chargement silencieuse
      } finally {
        setLoading(false);
      }
    };
    fetchBeer();
  }, [slug]);

  useSEO(
    beer
      ? {
          title: `${beer.name} - Bière artisanale des Alpilles`,
          description: `${beer.name} (${beer.style}) : ${beer.short_description} Bière artisanale brassée à Saint-Rémy-de-Provence, au cœur des Alpilles. ${beer.abv ? `ABV ${beer.abv}.` : ''}`,
          keywords: `${beer.name} bière artisanale, ${beer.style}, bière artisanale Alpilles, bière artisanale Saint-Rémy-de-Provence, bière locale Provence`,
          canonical: `/nos-bieres/${slug}`,
          og: { type: 'product', image: beer.image_url },
        }
      : { title: 'Bière introuvable', canonical: '/nos-bieres' }
  );

  useProductJsonLd(beer);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-foreground/60">Chargement...</p>
      </div>
    );
  }

  if (!beer) {
    return (
      <div className="py-32 text-center">
        <h1 className="text-4xl font-heading text-foreground mb-4">Bière introuvable</h1>
        <p className="text-foreground/70 mb-8">Cette bière n'existe pas ou n'est plus disponible.</p>
        <Link to="/nos-bieres">
          <Button className="rounded-full px-8 py-6" data-testid="back-to-gamme">Voir toute la gamme</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Fil d'ariane */}
        <nav className="text-sm text-foreground/60 mb-8" aria-label="Fil d'ariane" data-testid="breadcrumb">
          <Link to="/" className="hover:text-primary">Accueil</Link>
          <span className="mx-2">/</span>
          <Link to="/nos-bieres" className="hover:text-primary">Nos bières</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">{beer.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="rounded-2xl overflow-hidden shadow-xl border border-stone-200">
            <img
              src={beer.image_url}
              alt={`${beer.name} - ${beer.style} - bière artisanale Soif de Provence`}
              className="w-full h-full object-cover"
              data-testid="beer-detail-image"
            />
          </div>

          <div>
            <span className="inline-block text-accent font-semibold uppercase tracking-wide text-sm mb-2">{beer.style}</span>
            <h1 className="text-5xl lg:text-6xl font-heading text-foreground mb-4" data-testid="beer-detail-name">{beer.name}</h1>
            <p className="text-lg text-foreground/70 mb-6">{beer.short_description}</p>

            {(beer.abv || beer.ibu) && (
              <div className="flex flex-wrap gap-4 mb-8">
                {beer.abv && (
                  <div className="bg-secondary border border-border rounded-xl px-5 py-3 text-center">
                    <div className="text-xs text-foreground/50 uppercase">Alcool</div>
                    <div className="text-xl font-semibold text-foreground">{beer.abv}</div>
                  </div>
                )}
                {beer.ibu && (
                  <div className="bg-secondary border border-border rounded-xl px-5 py-3 text-center">
                    <div className="text-xs text-foreground/50 uppercase">Amertume (IBU)</div>
                    <div className="text-xl font-semibold text-foreground">{beer.ibu}</div>
                  </div>
                )}
                <div className="bg-secondary border border-border rounded-xl px-5 py-3 text-center">
                  <div className="text-xs text-foreground/50 uppercase">Format</div>
                  <div className="text-xl font-semibold text-foreground">{beer.format || '33cl'}</div>
                </div>
              </div>
            )}

            <div className="prose prose-lg max-w-none text-foreground/80 mb-8 whitespace-pre-line">
              {beer.long_description}
            </div>

            {beer.allergens && (
              <p className="text-sm text-foreground/60 mb-8"><strong>Allergènes :</strong> {beer.allergens}</p>
            )}

            <div className="bg-secondary border border-border rounded-2xl p-5 mb-8">
              <p className="text-sm text-foreground/70">{AVAILABILITY_MESSAGE}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/points-de-vente" className="flex-1">
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-6" data-testid="detail-points-de-vente">
                  <MapPin className="w-5 h-5 mr-2" />
                  Trouver un point de vente
                </Button>
              </Link>
              <Link to="/contact" className="flex-1">
                <Button variant="outline" className="w-full rounded-full py-6 border-2" data-testid="detail-contact">
                  <Mail className="w-5 h-5 mr-2" />
                  Nous contacter
                </Button>
              </Link>
              <Link to="/professionnels" className="flex-1">
                <Button variant="outline" className="w-full rounded-full py-6 border-2" data-testid="detail-devis-pro">
                  <FileText className="w-5 h-5 mr-2" />
                  Devis professionnels
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <Link to="/nos-bieres" className="inline-flex items-center text-primary font-medium hover:underline" data-testid="back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à toute la gamme
          </Link>
        </div>
      </div>
    </div>
  );
}
